import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader, CheckCircle, AlertTriangle, Users, Home } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SpeechInput } from '../components/SpeechInput';

interface Message {
  role: 'assistant' | 'user';
  content: string;
}

interface Score {
  total: number;
  categories: {
    lifestyle: number;
    preferences: number;
    dealbreakers: number;
    schedule: number;
    cleanliness: number;
    social: number;
  };
}

function OnboardingPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [applicationText, setApplicationText] = useState('');
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [preferenceType, setPreferenceType] = useState<'roommate' | 'solo'>('roommate');
  const [score, setScore] = useState<Score>({
    total: 0,
    categories: {
      lifestyle: 0,
      preferences: 0,
      dealbreakers: 0,
      schedule: 0,
      cleanliness: 0,
      social: 0
    }
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/auth');
      return;
    }
  };

  const handleApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationText.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          application_text: applicationText,
          moderation_status: 'pending'
        })
        .eq('id', session.user.id);

      if (profileError) throw profileError;

      setApplicationSubmitted(true);
      setShowChat(true);
      setMessages([
        { 
          role: 'assistant', 
          content: preferenceType === 'roommate' 
            ? `Thanks for sharing that! I'd like to ask you some specific questions about your roommate preferences to make sure we find the perfect match. Let's start with your budget - what's your ideal monthly rent range?`
            : `Thanks for sharing that! I'd like to ask you some specific questions about your solo living preferences. Let's start with your budget - what's your ideal monthly rent range for a studio or one-bedroom apartment?`
        }
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit application');
    } finally {
      setIsLoading(false);
    }
  };

  const updateScore = (response: string) => {
    const scoreMatch = response.match(/\[SCORE:(.*?)\]/);
    if (scoreMatch) {
      try {
        const scoreData = JSON.parse(scoreMatch[1]);
        setScore(scoreData);
      } catch (err) {
        console.error('Error parsing score:', err);
      }
    }
  };

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setError(null);

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [...messages, { role: 'user', content: userMessage }],
          action: 'continue',
          type: preferenceType
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get response');

      const assistantMessage = { role: 'assistant' as const, content: data.response };
      setMessages(prev => [...prev, assistantMessage]);

      updateScore(data.response);

      if (data.response.includes('[COMPLETE]')) {
        setShowSubmitButton(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreferencesSubmit = () => {
    navigate(preferenceType === 'roommate' ? '/matches' : '/lonestay');
  };

  const renderScoreCard = () => {
    if (score.total === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <h3 className="text-lg font-semibold mb-2">Profile Completion Score</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span>Overall Score</span>
            <div className="flex items-center">
              <div className="w-32 h-2 bg-gray-200 rounded-full mr-2">
                <div 
                  className="h-2 bg-primary-400 rounded-full" 
                  style={{ width: `${score.total}%` }}
                />
              </div>
              <span className="text-sm font-medium">{score.total}%</span>
            </div>
          </div>
          {Object.entries(score.categories).map(([category, value]) => (
            <div key={category} className="flex justify-between items-center text-sm">
              <span className="capitalize">{category}</span>
              <div className="flex items-center">
                <div className="w-24 h-1.5 bg-gray-200 rounded-full mr-2">
                  <div 
                    className="h-1.5 bg-primary-300 rounded-full" 
                    style={{ width: `${value}%` }}
                  />
                </div>
                <span className="text-xs">{value}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!applicationSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream-100 to-cream-200 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-serif text-primary-800 mb-4">
              Tell us about yourself
            </h2>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-700 mb-4">What type of living arrangement are you looking for?</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPreferenceType('roommate')}
                  className={`p-6 border rounded-lg flex flex-col items-center gap-3 transition-colors ${
                    preferenceType === 'roommate'
                      ? 'border-primary-400 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-primary-200'
                  }`}
                >
                  <Users className="w-8 h-8" />
                  <span className="font-medium">Find a Roommate</span>
                </button>
                <button
                  onClick={() => setPreferenceType('solo')}
                  className={`p-6 border rounded-lg flex flex-col items-center gap-3 transition-colors ${
                    preferenceType === 'solo'
                      ? 'border-primary-400 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-primary-200'
                  }`}
                >
                  <Home className="w-8 h-8" />
                  <span className="font-medium">Solo Living</span>
                </button>
              </div>
            </div>

            <p className="text-primary-600 mb-6">
              The more details you share, the better we can match you with your perfect living situation. Be open about your lifestyle, preferences, and what matters most to you - from your morning routine to your ideal Friday night. Your honesty helps us create meaningful connections.
            </p>

            {error && (
              <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleApplicationSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About You & What You're Looking For
                </label>
                <SpeechInput
                  onTranscript={setApplicationText}
                  placeholder={preferenceType === 'roommate' 
                    ? "Example: I'm looking for a 2-bedroom apartment in Brooklyn, preferably in Williamsburg or Greenpoint. My budget is around $3000/month. I'd like a roommate who is clean, respectful, and shares similar interests in music and art. I work from home sometimes, so good internet and a quiet environment are important..."
                    : "Example: I'm looking for a studio or one-bedroom apartment in Brooklyn, preferably in Williamsburg or Greenpoint. My budget is around $2500/month. I work from home sometimes, so good internet and a quiet environment are important. I enjoy having my own space but also want to be in a building with good amenities..."
                  }
                  defaultValue={applicationText}
                />
              </div>

              <div className="text-sm text-gray-600 bg-cream-100 p-4 rounded-lg">
                <p className="mb-2">
                  <strong>Why we review applications:</strong>
                </p>
                <p>
                  We're building a thoughtful, respectful community of people who care about creating great living spaces. Our review process helps ensure everyone contributes positively to the community.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || !applicationText.trim()}
                className="w-full py-3 bg-primary-400 text-white rounded-lg hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-100 to-cream-200 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {renderScoreCard()}
          <div className="h-[600px] overflow-y-auto p-6">
            <div className="space-y-6">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === 'assistant' ? 'justify-start' : 'justify-end'
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      message.role === 'assistant'
                        ? 'bg-cream-100 text-primary-800'
                        : 'bg-primary-400 text-white'
                    }`}
                  >
                    <p className="font-serif whitespace-pre-wrap">
                      {message.content.replace(/\[SCORE:.*?\]|\[COMPLETE\]/g, '')}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-cream-100 rounded-lg p-4">
                    <Loader className="w-5 h-5 animate-spin text-primary-400" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="border-t border-cream-200 p-4">
            {error && (
              <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 rounded">
                {error}
              </div>
            )}
            <form onSubmit={handleChatSubmit} className="space-y-4">
              <SpeechInput
                onTranscript={setInput}
                placeholder="Type or speak your response..."
                defaultValue={input}
                className="mb-4"
              />

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className="flex-1 px-4 py-2 bg-primary-400 text-white rounded-md hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>

              {showSubmitButton && (
                <button
                  type="button"
                  onClick={handlePreferencesSubmit}
                  className="w-full py-3 bg-primary-400 text-white rounded-lg hover:bg-primary-500 flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Continue to {preferenceType === 'roommate' ? 'Matches' : 'LoneStay'}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingPage;